import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, Sunrise, Heart, HandHeart, BookOpen, UserCircle2, LogOut, ShieldCheck } from "lucide-react";

const navItems = [
  { to: "/", label: "Inicio", icon: Sunrise },
  { to: "/archivo", label: "Archivo", icon: BookOpen },
  { to: "/oracion", label: "Muro de Oración", icon: HandHeart },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-200 hover:text-primary ${
      isActive ? "text-primary" : "text-muted-foreground"
    }`;

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img
            src="/brand/logo-gradient.png"
            alt="Tu Iglesia App"
            className="h-11 w-auto"
          />
          <span className="hidden h-8 w-px bg-border sm:block" />
          <span className="font-serif text-base font-bold leading-tight text-primary sm:text-lg">
            Centro de Fortaleza<br className="sm:hidden" /> Espiritual
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}-link`}>
              {item.label}
            </NavLink>
          ))}
          {user && (
            <NavLink to="/favoritos" className={linkClass} data-testid="nav-favoritos-link">
              Favoritos
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin-link">
              Panel Admin
            </NavLink>
          )}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="header-user-menu-button">
                  <UserCircle2 className="h-5 w-5 text-primary" />
                  <span className="max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Hola, {user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/cuenta")} data-testid="user-menu-cuenta">
                  <UserCircle2 className="mr-2 h-4 w-4" /> Mi cuenta
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favoritos")} data-testid="user-menu-favoritos">
                  <Heart className="mr-2 h-4 w-4" /> Mis favoritos
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="user-menu-admin">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Panel Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="user-menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-button">
                Iniciar sesión
              </Button>
              <Button onClick={() => navigate("/registro")} data-testid="header-register-button">
                Crear cuenta
              </Button>
            </>
          )}
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" data-testid="header-mobile-menu-button">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 bg-background">
            <SheetTitle className="font-serif text-lg">Menú</SheetTitle>
            <nav className="mt-6 flex flex-col gap-4">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 rounded-md px-2 py-2 text-base font-medium hover:bg-muted"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, "-")}-link`}
                >
                  <item.icon className="h-5 w-5 text-primary" /> {item.label}
                </Link>
              ))}
              {user && (
                <>
                  <Link to="/favoritos" onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-md px-2 py-2 text-base font-medium hover:bg-muted" data-testid="mobile-nav-favoritos-link">
                    <Heart className="h-5 w-5 text-primary" /> Favoritos
                  </Link>
                  <Link to="/cuenta" onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-md px-2 py-2 text-base font-medium hover:bg-muted" data-testid="mobile-nav-cuenta-link">
                    <UserCircle2 className="h-5 w-5 text-primary" /> Mi cuenta
                  </Link>
                </>
              )}
              {user?.is_admin && (
                <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-md px-2 py-2 text-base font-medium hover:bg-muted" data-testid="mobile-nav-admin-link">
                  <ShieldCheck className="h-5 w-5 text-primary" /> Panel Admin
                </Link>
              )}
              <div className="mt-4 border-t border-border pt-4">
                {user ? (
                  <Button variant="outline" className="w-full" onClick={() => { handleLogout(); setOpen(false); }} data-testid="mobile-logout-button">
                    <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                  </Button>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Button className="w-full" onClick={() => { navigate("/login"); setOpen(false); }} data-testid="mobile-login-button">
                      Iniciar sesión
                    </Button>
                    <Button variant="outline" className="w-full" onClick={() => { navigate("/registro"); setOpen(false); }} data-testid="mobile-register-button">
                      Crear cuenta
                    </Button>
                  </div>
                )}
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
